/**
 * The preview's renderer (1.I-2 deliverable 28), pure, over the stored body JSON: the static
 * render a peek paints before its editor exists. It writes the editor's markup, so the editor's
 * stylesheet lays it out as the editor will: the element and `data-type` each block renders as,
 * `data-id` on every block that has one, the `cx-editor` classes, and `decorationClass` for
 * read-aloud boxes, secrets, GM notes and callouts. doc-model's `html` target is not used: it
 * writes Read mode's markup and `data-block-id`.
 *
 * - Collapsible blocks are drawn as the editor opens them: toggles closed (1.E-1 ruling 12),
 *   secrets and branch groups open, GM notes and topics by their `collapsed` attribute; a nest
 *   after a secret or a GM note is the hidden nest, collapsed with a collapsed owner.
 * - Nothing in it takes input or focus: no `button`, no `input`, no `href`, no `contenteditable`,
 *   no `tabindex`, and no test id (a chip here is never the editor's chip).
 * - Top-level blocks are drawn until `PREVIEW_TEXT_BUDGET` characters of text have been written
 *   (the preview covers the first screens; the editor replaces it within a few frames).
 *
 * The output is a tree of plain values; `peek-preview.tsx` turns it into React elements.
 */
import { checkboxClass, decorationClass } from '@codex/ui';

export interface PreviewElement {
  readonly tag: string;
  readonly attrs: Readonly<Record<string, string>>;
  readonly children: readonly PreviewNode[];
}
export type PreviewNode = PreviewElement | string;

/** How much text the preview draws before it stops adding top-level blocks. */
export const PREVIEW_TEXT_BUDGET = 12_000;

interface JsonNode {
  readonly type?: unknown;
  readonly attrs?: Readonly<Record<string, unknown>>;
  readonly content?: unknown;
  readonly text?: unknown;
  readonly marks?: unknown;
}

const asNode = (value: unknown): JsonNode | null =>
  typeof value === 'object' && value !== null && !Array.isArray(value) ? (value as JsonNode) : null;

const childrenOf = (node: JsonNode): JsonNode[] =>
  Array.isArray(node.content)
    ? node.content.map(asNode).filter((n): n is JsonNode => n !== null)
    : [];

const str = (value: unknown): string | null =>
  typeof value === 'string' && value !== '' ? value : null;

const attr = (node: JsonNode, key: string): unknown => node.attrs?.[key];

const h = (
  tag: string,
  attrs: Record<string, string | null | undefined>,
  children: readonly PreviewNode[] = [],
): PreviewElement => {
  const clean: Record<string, string> = {};
  for (const [key, value] of Object.entries(attrs)) if (value != null) clean[key] = value;
  return { tag, attrs: clean, children };
};

/** The attributes every block carries: its type and, when it has one, its id. */
const block = (node: JsonNode, extra: Record<string, string | null | undefined> = {}) => ({
  'data-type': String(node.type),
  'data-id': str(attr(node, 'id')),
  ...extra,
});

/** `inlineCheck`'s label, as doc-model's `checkLabel` writes it. */
export function checkText(attrs: Readonly<Record<string, unknown>> | undefined): string {
  const a = attrs ?? {};
  const dc = typeof a.dc === 'number' ? ` DC ${a.dc}` : '';
  if (a.kind === 'save') return `${str(a.ability) ?? ''} save${dc}`.trim();
  if (a.kind === 'roll') return str(a.formula) ?? 'roll';
  return `${str(a.skill) ?? str(a.ability) ?? 'check'}${dc}`;
}

const MARK_TAGS: Readonly<Record<string, string>> = {
  bold: 'strong',
  italic: 'em',
  underline: 'u',
  strike: 's',
  code: 'code',
  link: 'a',
  highlight: 'mark',
};

function marked(text: string, marks: unknown): PreviewNode {
  let out: PreviewNode = text;
  const list = Array.isArray(marks) ? marks.map(asNode) : [];
  for (const mark of [...list].reverse()) {
    if (!mark) continue;
    const type = String(mark.type);
    const color = str(attr(mark, 'color'));
    out =
      type in MARK_TAGS
        ? h(MARK_TAGS[type] as string, { 'data-color': color }, [out])
        : h('span', { 'data-mark': type, 'data-color': color }, [out]);
  }
  return out;
}

class Writer {
  written = 0;

  inline(nodes: readonly JsonNode[]): PreviewNode[] {
    return nodes.map((node) => this.inlineNode(node));
  }

  private text(value: string): string {
    this.written += value.length;
    return value;
  }

  private inlineNode(node: JsonNode): PreviewNode {
    switch (node.type) {
      case 'text':
        return marked(this.text(typeof node.text === 'string' ? node.text : ''), node.marks);
      case 'hardBreak':
        return h('br', {});
      case 'mention':
        return h('span', { class: decorationClass.mention, 'data-type': 'mention' }, [
          h('span', { class: 'cx-chip cx-chip--sm' }, [
            h('span', { class: 'cx-chip__label' }, [
              this.text(str(attr(node, 'label')) ?? 'Untitled'),
            ]),
          ]),
        ]);
      case 'dateRef':
        return h('span', { class: decorationClass.date, 'data-type': 'dateRef' }, [
          this.text(str(attr(node, 'raw')) ?? ''),
        ]);
      case 'inlineCheck':
        return h('span', { class: decorationClass.inlineCheck, 'data-type': 'inlineCheck' }, [
          h('span', { class: 'cx-inline-check__label' }, [this.text(checkText(node.attrs))]),
        ]);
      default:
        return '';
    }
  }

  /**
   * The blocks of one container, with the hidden-nest rule for what follows a secret or GM note;
   * no further block once `budget` characters of text are written.
   */
  blocks(nodes: readonly JsonNode[], budget = Number.POSITIVE_INFINITY): PreviewNode[] {
    const out: PreviewNode[] = [];
    // The owner of the nest chain under way: null, or whether that secret or GM note is collapsed.
    let owner: boolean | null = null;
    for (const node of nodes) {
      if (this.written >= budget) break;
      if (node.type === 'nest' && owner !== null) {
        const classes = `cx-editor__nest cx-editor__nest--hidden${owner ? ' cx-editor__nest--collapsed' : ''}`;
        out.push(this.container(node, 'div', { class: classes, role: 'group' }));
        continue;
      }
      owner = null;
      if (node.type === 'secret') owner = false;
      if (node.type === 'gmNote') owner = attr(node, 'collapsed') === true;
      out.push(this.blockNode(node));
    }
    return out;
  }

  private container(
    node: JsonNode,
    tag: string,
    extra: Record<string, string | null | undefined> = {},
  ): PreviewElement {
    return h(tag, block(node, extra), this.blocks(childrenOf(node)));
  }

  private textblock(
    node: JsonNode,
    tag: string,
    extra: Record<string, string | null | undefined> = {},
  ): PreviewElement {
    return h(tag, block(node, extra), this.inline(childrenOf(node)));
  }

  private headed(
    node: JsonNode,
    className: string,
    head: { className: string; text: string } | null,
    bodyClass: string | null,
    extra: Record<string, string | null | undefined> = {},
  ): PreviewElement {
    const body = this.blocks(childrenOf(node));
    const parts: PreviewNode[] = [];
    if (head) parts.push(h('span', { class: head.className }, [head.text]));
    if (bodyClass) parts.push(h('div', { class: bodyClass }, body));
    else parts.push(...body);
    return h('div', block(node, { class: className, ...extra }), parts);
  }

  blockNode(node: JsonNode): PreviewNode {
    switch (node.type) {
      case 'paragraph':
        return this.textblock(node, 'p');
      case 'heading': {
        const level = attr(node, 'level');
        const n = level === 2 || level === 3 ? level : 1;
        return this.textblock(node, `h${n}`);
      }
      case 'codeBlock':
        return h('pre', block(node), [h('code', {}, this.inline(childrenOf(node)))]);
      case 'horizontalRule':
        return h('hr', block(node));
      case 'blockquote':
        return this.container(node, 'blockquote');
      case 'bulletList':
        return this.container(node, 'ul');
      case 'orderedList': {
        const start = attr(node, 'start');
        return this.container(node, 'ol', {
          start: typeof start === 'number' && start !== 1 ? String(start) : null,
        });
      }
      case 'listItem':
        return this.container(node, 'li');
      case 'taskList':
        return this.container(node, 'ul');
      case 'taskItem':
        return h('li', block(node, { 'data-checked': String(attr(node, 'checked') === true) }), [
          h('label', { class: `${checkboxClass.root} ${checkboxClass.bare}` }, [
            h('span', { class: checkboxClass.box, 'aria-hidden': 'true' }),
          ]),
          h('div', {}, this.blocks(childrenOf(node))),
        ]);
      case 'table':
        return h('table', block(node), [
          h(
            'tbody',
            {},
            childrenOf(node).map((row) =>
              h(
                'tr',
                block(row),
                childrenOf(row).map((cell) =>
                  this.container(cell, cell.type === 'tableHeader' ? 'th' : 'td', {
                    colspan: span(attr(cell, 'colspan')),
                    rowspan: span(attr(cell, 'rowspan')),
                  }),
                ),
              ),
            ),
          ),
        ]);
      case 'nest':
        return this.container(node, 'div', { class: 'cx-editor__nest', role: 'group' });
      case 'columns':
        return this.container(node, 'div', { class: 'cx-editor__columns' });
      case 'column':
        return this.container(node, 'div', { class: 'cx-editor__column' });
      case 'details': {
        // Closed when a document opens: the CSS hides the content, as in the editor.
        const parts = childrenOf(node).map((child) =>
          child.type === 'detailsSummary'
            ? h('summary', { 'data-type': 'detailsSummary' }, this.inline(childrenOf(child)))
            : this.container(child, 'div'),
        );
        return h(
          'div',
          block(node, {
            class: 'cx-editor__toggle',
            role: 'group',
            'aria-label': 'Toggle',
            'data-level': String(attr(node, 'level') ?? 0),
          }),
          [
            h('span', { class: 'cx-editor__toggle-button', 'aria-hidden': 'true' }),
            h('div', { class: 'cx-editor__toggle-body' }, parts),
          ],
        );
      }
      case 'callout':
        return h(
          'div',
          block(node, {
            class: decorationClass.callout,
            role: 'note',
            'aria-label': 'Callout',
            'data-tone': str(attr(node, 'tone')),
          }),
          [
            h('span', { class: 'cx-callout__icon', 'aria-hidden': 'true' }, [
              str(attr(node, 'icon')) ?? '',
            ]),
            h('div', { class: 'cx-callout__content' }, this.blocks(childrenOf(node))),
          ],
        );
      case 'readAloud':
        return this.container(node, 'div', {
          class: decorationClass.readAloud,
          role: 'group',
          'aria-label': 'Read aloud',
        });
      case 'secret':
        // Open when a document opens.
        return this.headed(
          node,
          decorationClass.secret,
          { className: decorationClass.secretToggle, text: 'Secret' },
          decorationClass.secretBody,
          { role: 'group', 'aria-label': 'Secret', 'data-expanded': '' },
        );
      case 'gmNote':
        return this.headed(
          node,
          decorationClass.gmNote,
          { className: decorationClass.gmNoteLabel, text: 'GM note' },
          decorationClass.gmNoteBody,
          {
            role: 'group',
            'aria-label': 'GM note',
            'data-collapsed': attr(node, 'collapsed') === true ? '' : null,
          },
        );
      case 'sceneHeading':
        return this.textblock(node, 'div', {
          class: decorationClass.sceneHeading,
          role: 'heading',
          'aria-level': '2',
        });
      case 'stageDirection':
        return this.textblock(node, 'div', { class: decorationClass.stageDirection });
      case 'dialogue':
        return h('div', block(node, { class: decorationClass.dialogue }), [
          h('span', { class: decorationClass.dialogueSpeaker }, [
            str(attr(node, 'speakerName')) ?? '',
          ]),
          h('div', { class: decorationClass.dialogueLine }, this.inline(childrenOf(node))),
        ]);
      case 'topic':
        return this.headed(
          node,
          decorationClass.topic,
          { className: decorationClass.topicHead, text: str(attr(node, 'topic')) ?? '' },
          'cx-topic__body',
          { 'data-collapsed': attr(node, 'collapsed') === true ? '' : null },
        );
      case 'branchGroup':
        return this.headed(node, decorationClass.branchGroup, null, 'cx-branch-group__body');
      case 'branchOption':
        return this.headed(
          node,
          decorationClass.branchOption,
          {
            className: decorationClass.branchOptionHead,
            text: [str(attr(node, 'label')), str(attr(node, 'outcome'))]
              .filter((v) => v !== null)
              .join(' · '),
          },
          'cx-branch-option__body',
        );
      case 'image':
      case 'statblockEmbed':
      case 'ruleCardEmbed':
      case 'relationList':
        return h('div', block(node, { class: 'cx-editor__atom' }), [
          h('span', { class: 'cx-editor__atom-label' }, [atomLabel(node)]),
        ]);
      default:
        // A block this renderer does not know: its text, so nothing written is missing (a
        // wrapper keeps its blocks, a textblock its inline content).
        return childrenOf(node).length > 0 &&
          childrenOf(node).every((c) => INLINE_TYPES.has(String(c.type)))
          ? this.textblock(node, 'div')
          : this.container(node, 'div');
    }
  }
}

/** The node types `inlineNode` writes. */
const INLINE_TYPES: ReadonlySet<string> = new Set([
  'text',
  'hardBreak',
  'mention',
  'dateRef',
  'inlineCheck',
]);

const span = (value: unknown): string | null =>
  typeof value === 'number' && value > 1 ? String(value) : null;

const ATOM_NAMES: Readonly<Record<string, string>> = {
  image: 'Image',
  statblockEmbed: 'Stat block',
  ruleCardEmbed: 'Rule card',
  relationList: 'Relation list',
};

function atomLabel(node: JsonNode): string {
  const name = ATOM_NAMES[String(node.type)] ?? '';
  const alt = node.type === 'image' ? str(attr(node, 'alt')) : null;
  return alt ? `${name}: ${alt}` : name;
}

/** The body's top-level blocks as the editor draws them, up to `PREVIEW_TEXT_BUDGET` of text. */
export function renderPreviewBody(body: unknown, budget = PREVIEW_TEXT_BUDGET): PreviewNode[] {
  const doc = asNode(body);
  return doc ? new Writer().blocks(childrenOf(doc), budget) : [];
}
